"use client";

import { useState, useEffect, useTransition } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Filter, ArrowUpDown, Search, Loader2 } from "lucide-react";

export function SpacControls() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentStatus = searchParams.get("status") || "all";
  const currentSort = searchParams.get("sort") || "recent";
  const currentSector = searchParams.get("sector") || "all";
  const initialQuery = searchParams.get("q") || "";
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== (searchParams.get("q") || "")) {
        const params = new URLSearchParams(searchParams.toString());
        if (searchTerm) {
          params.set("q", searchTerm);
        } else {
          params.delete("q");
        }
        params.set("page", "1");
        startTransition(() => {
          router.push(`${pathname}?${params.toString()}`, { scroll: false });
        });
      }
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, pathname, router, searchParams]);

  const createQueryString = (name: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(name, value);
    params.set("page", "1"); // Reset to page 1 on filter/sort change
    return params.toString();
  };

  return (
    <div className="flex flex-col gap-4 mb-6 w-full relative">
      <div className="flex items-center gap-2 bg-muted/30 border border-border rounded-xl px-4 py-2 w-full shadow-sm focus-within:ring-2 focus-within:ring-primary/50 transition-all">
        {isPending ? (
          <Loader2 className="w-5 h-5 shrink-0 text-muted-foreground animate-spin" />
        ) : (
          <Search className="w-5 h-5 shrink-0 text-muted-foreground" />
        )}
        <input
          type="text"
          placeholder="Search by firm name, ticker, sponsor, or executives..."
          className="bg-transparent border-none text-sm font-medium text-foreground outline-none focus:ring-0 w-full placeholder:text-muted-foreground/60 w-full"
          style={{ boxShadow: "none" }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full opacity-100 transition-opacity"
        style={{ opacity: isPending ? 0.6 : 1 }}
      >
        <div className="flex items-center gap-2 bg-muted/30 border border-border rounded-xl px-3 py-2 flex-1 shadow-sm focus-within:ring-2 focus-within:ring-primary/50 transition-all w-full sm:w-auto">
          <Filter className="w-4 h-4 shrink-0 text-muted-foreground" />
          <select
            title="Filter by Status"
            className="bg-transparent border-none text-sm font-medium text-foreground outline-none focus:ring-0 cursor-pointer w-full sm:min-w-[140px]"
            value={currentStatus}
            onChange={(e) => {
              startTransition(() => {
                router.push(
                  `${pathname}?${createQueryString("status", e.target.value)}`,
                  { scroll: false },
                );
              });
            }}
          >
            <option value="all">All Statuses</option>
            <option value="searching">Searching (Active)</option>
            <option value="pre-ipo">Pre-IPO</option>
            <option value="announced">Definitive Agreement</option>
            <option value="completed">Completed / Merged</option>
            <option value="liquidated">Liquidated</option>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-muted/30 border border-border rounded-xl px-3 py-2 flex-1 shadow-sm focus-within:ring-2 focus-within:ring-primary/50 transition-all w-full sm:w-auto">
          <Filter className="w-4 h-4 shrink-0 text-muted-foreground" />
          <select
            title="Filter by Sector"
            className="bg-transparent border-none text-sm font-medium text-foreground outline-none focus:ring-0 cursor-pointer w-full sm:min-w-[140px]"
            value={currentSector}
            onChange={(e) => {
              startTransition(() => {
                router.push(
                  `${pathname}?${createQueryString("sector", e.target.value)}`,
                  { scroll: false },
                );
              });
            }}
          >
            <option value="all">All Sectors</option>
            <option value="Technology">Technology</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Financials">Financials</option>
            <option value="Energy">Energy</option>
            <option value="Consumer">Consumer</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Industrial">Industrial</option>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-muted/30 border border-border rounded-xl px-3 py-2 flex-1 shadow-sm focus-within:ring-2 focus-within:ring-primary/50 transition-all w-full sm:w-auto">
          <ArrowUpDown className="w-4 h-4 shrink-0 text-muted-foreground" />
          <select
            title="Sort Results"
            className="bg-transparent border-none text-sm font-medium text-foreground outline-none focus:ring-0 cursor-pointer w-full sm:min-w-[140px]"
            value={currentSort}
            onChange={(e) => {
              startTransition(() => {
                router.push(
                  `${pathname}?${createQueryString("sort", e.target.value)}`,
                  { scroll: false },
                );
              });
            }}
          >
            <option value="recent">Recently Added</option>
            <option value="trust-desc">Trust Size (High to Low)</option>
            <option value="trust-asc">Trust Size (Low to High)</option>
            <option value="name-asc">Alphabetical (A-Z)</option>
          </select>
        </div>
      </div>
    </div>
  );
}
